import classNames from "classnames";
import Select from "rc-select";
import "rc-select/assets/index.css";
import type { LabelInValueType } from "rc-select/lib/Select";
import Tree from "rc-tree";
import type { BasicDataNode, TreeNodeProps } from "rc-tree";
import type { EventDataNode, Key } from "rc-tree/lib/interface";
import React, { useMemo, useRef, useState } from "react";
import {
  KuikaPopupContainerGetter,
  useKuikaPopupContainer
} from "../kuika-popup-container/kuika-popup-container-context";
import "../kuika-selectbox/kuika-selectbox.scss";
import KuikaSvg from "../kuika-svg/kuika-svg";
import type { IKuikaTreeSelectSingleProps, ITreeSelectNode } from "./kuika-tree-select";
import "./kuika-tree-select-floating.scss";
import {
  buildTreeSelectIndex,
  collectExpandableTreeSelectIds,
  hasTreeSelectChildren,
  isTreeSelectNodeVisible,
  ITreeSelectIndex,
  ITreeSelectSearchResult,
  searchTreeSelectNodes
} from "./kuika-tree-select-model";

/**
 * rc-tree names every part of the tree from this prefix (`…-treenode`, `…-switcher`, `…-indent-unit`).
 * Not rc-tree's own `rc-tree`: the item tree imports `rc-tree/assets/index.css` globally, and its
 * sprite switchers and `line.gif` guides would land on this list whenever that panel had loaded.
 */
const TREE_PREFIX_CLS: string = "kuika-tree-select__tree";

/** `--row-h-compact`, which the rows are drawn at; rc-tree needs the number to scroll a row into view. */
const ROW_HEIGHT: number = 28;

const DEFAULT_LIST_HEIGHT: number = 256;

const SUGGESTED_ROW_CLASS: string = "kuika-tree-select__row--suggested";
const GROUP_ROW_CLASS: string = "kuika-tree-select__row--group";

/** A tree node as rc-tree takes it. The `aria-*`/`data-*` keys land on the row element. */
interface IKuikaTreeSelectDataNode extends BasicDataNode {
  key: string;
  /** Kept a plain string: rc-tree writes it as the row's `title` attribute only when it is one. */
  title: string;
  children?: IKuikaTreeSelectDataNode[];
  "aria-disabled"?: boolean;
  "data-testid"?: string;
}

interface IKuikaTreeSelectSelectInfo {
  node: EventDataNode<IKuikaTreeSelectDataNode>;
}

const toTreeKey = (key: Key | null): string | null => (key === null ? null : String(key));

/**
 * The floating single-pick mode of KuikaTreeSelect: KuikaSelectbox's field (an rc-select shell, its
 * search input, clear icon, portal and motion) over an rc-tree list rendered in place of rc-select's
 * own option list.
 *
 * rc-select hands its arrow and Enter keys to its option list through a ref that only the list it
 * renders itself fills. `dropdownRender` returns the tree without that list, so rc-select's own
 * keyboard handling finds nothing to drive, and the keys arrive here through `onInputKeyDown`.
 *
 * The keyboard, while the list is open:
 * - Typing filters: matches, their subtrees, and the ancestors that lead to them. Every ancestor of
 *   a match is opened.
 * - The SUGGESTED row is what Enter commits while the cursor is not in the list: the first match
 *   that can be picked while searching, else the current value's row when it is on screen. It is
 *   drawn as the cursor.
 * - The first ArrowDown/ArrowUp puts the cursor ON the suggested row rather than stepping past it,
 *   so "type, ArrowDown, Enter" and "type, Enter" commit the same row. After that the arrows are
 *   rc-tree's own: Up/Down walk every visible row (a disabled folder too, so it can be opened),
 *   Right opens a folder or steps into it, Left closes it or steps out.
 * - Enter on a row that can be picked commits it; on a disabled folder it opens or closes it.
 * - Escape closes the list. rc-select closes it from its own option list, which is not mounted.
 *
 * Moving the pointer over the rows takes the cursor away (rc-tree's own behaviour), which hands
 * Enter back to the suggested row.
 */
const KuikaTreeSelectFloating: React.FC<IKuikaTreeSelectSingleProps> = ({
  treeData,
  value,
  onChange,
  placeholder = "Select",
  disabled = false,
  className,
  maxHeight = DEFAULT_LIST_HEIGHT,
  searchable = false,
  allowClear = false,
  showLine = false,
  defaultExpandAll = false,
  size = "md",
  dataTestId,
  getPopupContainer
}: IKuikaTreeSelectSingleProps): JSX.Element => {
  const popupContainerFromContext: KuikaPopupContainerGetter | undefined = useKuikaPopupContainer();
  const resolvedGetPopupContainer: ((triggerNode: HTMLElement) => HTMLElement) | undefined =
    getPopupContainer ?? popupContainerFromContext;

  const treeRef: React.RefObject<Tree<IKuikaTreeSelectDataNode>> = useRef<Tree<IKuikaTreeSelectDataNode>>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  // The keyboard cursor, shared with rc-tree (`activeKey` / `onActiveChange`). `null` while the
  // cursor is not in the list — then the suggested row stands in for it.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [browseExpandedIds, setBrowseExpandedIds] = useState<string[]>((): string[] =>
    defaultExpandAll ? collectExpandableTreeSelectIds(treeData) : []
  );
  // What the reader opened or closed during the current search; `null` until they do, when the
  // search's own "every ancestor of a match" set applies. Reset by every change of the term.
  const [searchExpandedOverride, setSearchExpandedOverride] = useState<string[] | null>(null);

  const index: ITreeSelectIndex = useMemo((): ITreeSelectIndex => buildTreeSelectIndex(treeData), [treeData]);
  const search: ITreeSelectSearchResult | null = useMemo(
    (): ITreeSelectSearchResult | null => (searchValue === "" ? null : searchTreeSelectNodes(treeData, searchValue)),
    [treeData, searchValue]
  );

  const shownNodes: ITreeSelectNode[] = search === null ? treeData : search.nodes;
  let expandedIds: string[] = browseExpandedIds;
  if (search !== null) {
    expandedIds = searchExpandedOverride ?? search.expandedIds;
  }
  const expandedIdSet: ReadonlySet<string> = useMemo(
    (): ReadonlySet<string> => new Set<string>(expandedIds),
    [expandedIds]
  );

  const selectedId: string | null = value === undefined ? null : (index.idByPath.get(value) ?? null);

  let suggestionCandidateId: string | null = selectedId;
  if (search !== null) {
    suggestionCandidateId = search.firstEnabledMatchId;
  }
  // A row hidden in a closed folder is not what Enter should commit.
  const suggestedId: string | null =
    suggestionCandidateId !== null && isTreeSelectNodeVisible(index, suggestionCandidateId, expandedIdSet)
      ? suggestionCandidateId
      : null;

  const dataNodes: IKuikaTreeSelectDataNode[] = useMemo((): IKuikaTreeSelectDataNode[] => {
    const toDataNode = (node: ITreeSelectNode): IKuikaTreeSelectDataNode => {
      const expandable: boolean = hasTreeSelectChildren(node);
      const isDisabled: boolean = node.disabled === true;
      return {
        key: node.id,
        title: node.name,
        isLeaf: !expandable,
        disabled: isDisabled,
        className: classNames({
          [SUGGESTED_ROW_CLASS]: node.id === suggestedId,
          [GROUP_ROW_CLASS]: isDisabled && expandable
        }),
        // Only a row a click does nothing on is announced (and drawn) disabled: a disabled folder
        // still opens and closes.
        "aria-disabled": isDisabled && !expandable ? true : undefined,
        "data-testid": node.testId,
        children: expandable ? (node.children ?? []).map(toDataNode) : undefined
      };
    };
    return shownNodes.map(toDataNode);
  }, [shownNodes, suggestedId]);

  const selectedKeys: string[] = useMemo((): string[] => (selectedId === null ? [] : [selectedId]), [selectedId]);

  const resetListState = (): void => {
    setSearchValue("");
    setActiveKey(null);
    setSearchExpandedOverride(null);
  };

  const closeList = (): void => {
    setIsOpen(false);
    resetListState();
  };

  // Reached only for a node that can be picked: rc-tree does not report a click on a disabled row,
  // and Enter goes through `activateRow`.
  const commitNode = (node: ITreeSelectNode): void => {
    if (node.path !== value) {
      onChange(node.path);
    }
    closeList();
  };

  const setExpandedIds = (ids: string[]): void => {
    if (search !== null) {
      setSearchExpandedOverride(ids);
    } else {
      setBrowseExpandedIds(ids);
    }
  };

  const toggleExpanded = (id: string): void => {
    setExpandedIds(
      expandedIdSet.has(id) ? expandedIds.filter((key: string): boolean => key !== id) : [...expandedIds, id]
    );
  };

  // Enter on a row: commit it, or — for a folder that cannot be picked — do what a click does.
  const activateRow = (id: string): void => {
    const node: ITreeSelectNode | undefined = index.byId.get(id)?.node;
    if (node === undefined) return;
    if (node.disabled !== true) {
      commitNode(node);
    } else if (hasTreeSelectChildren(node)) {
      toggleExpanded(id);
    }
  };

  // rc-tree reads only `which` off the event and may call `preventDefault`.
  const forwardToTree = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    treeRef.current?.onKeyDown(event);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!isOpen || event.nativeEvent.isComposing) return;
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (activeKey === null && suggestedId !== null) {
          event.preventDefault();
          setActiveKey(suggestedId);
          return;
        }
        forwardToTree(event);
        return;
      case "ArrowLeft":
      case "ArrowRight":
        forwardToTree(event);
        return;
      case "Enter": {
        event.preventDefault();
        const targetId: string | null = activeKey ?? suggestedId;
        if (targetId !== null) {
          activateRow(targetId);
        }
        return;
      }
      case "Escape":
        closeList();
        return;
      default:
        return;
    }
  };

  // Opening keeps the term: typing into a closed field reports the first character to `onSearch`
  // BEFORE it asks for the list to open, and clearing it here would swallow that character.
  const handleVisibleChange = (nextOpen: boolean): void => {
    setIsOpen(nextOpen);
    if (nextOpen) {
      setActiveKey(null);
    } else {
      resetListState();
    }
  };

  const handleSearch = (text: string): void => {
    setSearchValue(text);
    setActiveKey(null);
    setSearchExpandedOverride(null);
  };

  const handleClear = (): void => {
    if (value !== undefined) {
      onChange(undefined);
    }
  };

  const handleTreeSelect = (_selectedKeys: Key[], info: IKuikaTreeSelectSelectInfo): void => {
    const node: ITreeSelectNode | undefined = index.byId.get(String(info.node.key))?.node;
    if (node !== undefined) {
      commitNode(node);
    }
  };

  const handleTreeExpand = (keys: Key[]): void => {
    setExpandedIds(keys.map((key: Key): string => String(key)));
  };

  const handleActiveChange = (key: Key | null): void => {
    setActiveKey(toTreeKey(key));
  };

  const renderLabel = (item: LabelInValueType): React.ReactNode => {
    const id: string | undefined = index.idByPath.get(String(item.value));
    const node: ITreeSelectNode | undefined = id === undefined ? undefined : index.byId.get(id)?.node;
    return node === undefined ? String(item.value) : node.name;
  };

  const renderSwitcherIcon = (nodeProps: TreeNodeProps): React.ReactNode => {
    if (nodeProps.isLeaf) {
      return showLine ? <span className="kuika-tree-select__leaf-line" /> : null;
    }
    return <KuikaSvg name="k-right" type="arrows" width={16} height={16} />;
  };

  // A press inside the list must not blur the search input: the cursor, the term and rc-select's
  // own open state all live on it.
  const keepInputFocus = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const renderList = (): React.ReactElement => (
    <div className="kuika-tree-select__popup-body" onMouseDown={keepInputFocus}>
      {shownNodes.length === 0 ? (
        <div className="kuika-tree-select__popup-empty">{search === null ? "No options" : "No matches"}</div>
      ) : (
        <Tree<IKuikaTreeSelectDataNode>
          ref={treeRef}
          prefixCls={TREE_PREFIX_CLS}
          treeData={dataNodes}
          expandedKeys={expandedIds}
          selectedKeys={selectedKeys}
          activeKey={activeKey}
          onActiveChange={handleActiveChange}
          onExpand={handleTreeExpand}
          onSelect={handleTreeSelect}
          expandAction="click"
          showLine={showLine}
          showIcon={false}
          switcherIcon={renderSwitcherIcon}
          focusable={false}
          height={maxHeight}
          itemHeight={ROW_HEIGHT}
          virtual={false}
        />
      )}
    </div>
  );

  return (
    <Select
      className={classNames("kuika-selectbox", `kuika-selectbox--${size}`, "kuika-tree-select--floating", className)}
      // The selectbox's dropdown class carries the kit's floating surface and motion, and is what
      // KModal / KuikaDrawer look for to let Escape close this list rather than themselves.
      dropdownClassName={classNames("kuika-selectbox__dropdown", "kuika-tree-select__popup")}
      transitionName="kuika-dropdown-motion"
      data-testid={dataTestId}
      value={value}
      labelRender={renderLabel}
      placeholder={placeholder}
      disabled={disabled}
      showSearch={searchable}
      searchValue={searchValue}
      onSearch={handleSearch}
      open={isOpen}
      onDropdownVisibleChange={handleVisibleChange}
      onInputKeyDown={handleInputKeyDown}
      dropdownRender={renderList}
      suffixIcon={<KuikaSvg name="chevron-down" type="arrows" width={12} height={12} />}
      allowClear={allowClear ? { clearIcon: <KuikaSvg name="x" width={12} height={12} /> } : false}
      onClear={handleClear}
      getPopupContainer={resolvedGetPopupContainer}
    />
  );
};

export default KuikaTreeSelectFloating;
